import { Link, useNavigate } from "react-router-dom";
import useStore from "../store/useStore";
import { useAllShows } from "../hooks/useShows";
import { NotebookPen, X } from "lucide-react";
import { toast } from "sonner";
import Poster from "../components/Poster";
import { TOAST_MS } from "../lib/constants";
import { lower } from "../lib/strings";

export default function Saved() {
  const { savedShows, removeSave } = useStore();
  const navigate = useNavigate();
  const allShows = useAllShows();
  const shows = savedShows.map((id) => allShows.find((s) => s.id === id)).filter(Boolean);

  return (
    <main data-testid="saved-page" className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
          saved
        </div>
        <h1 className="font-display text-5xl leading-none mt-1">
          saved<span className="text-acid">.</span>
        </h1>
        <div className="font-mono text-xs text-white/50 mt-2">
          {shows.length} {shows.length === 1 ? "show" : "shows"}
        </div>
      </header>

      {shows.length === 0 ? (
        <div className="px-5 py-16 text-center">
          <p className="font-display text-2xl text-white/70 leading-tight">
            nothing saved yet.
          </p>
          <p className="font-mono text-xs text-white/40 mt-3 leading-relaxed">
            save shows from <Link to="/toronto" className="text-acid underline">toronto</Link>.
          </p>
        </div>
      ) : (
        <ul>
          {shows.map((s) => (
            <li
              key={s.id}
              data-testid={`saved-item-${s.id}`}
              className="border-b-2 border-white/15 px-5 py-5"
            >
              <Link to={`/show/${s.id}`} className="flex gap-4 items-start">
                <Poster show={s} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-1">
                    {lower(s.theatre)}
                  </div>
                  <h2 className="font-display text-xl leading-tight hover:text-acid transition-colors break-words">
                    {lower(s.title)}
                  </h2>
                  <div className="font-mono text-[11px] text-white/50 mt-1">
                    {lower(s.dateLabel)}
                  </div>
                </div>
              </Link>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <button
                  data-testid={`saved-mark-seen-${s.id}`}
                  onClick={() => navigate(`/log/${s.id}`)}
                  className="font-mono text-[11px] lowercase border-2 border-acid text-acid px-2 py-2 hover:bg-acid hover:text-black transition-colors flex items-center justify-center gap-1"
                >
                  <NotebookPen className="w-3 h-3" /> log
                </button>
                <button
                  data-testid={`saved-open-${s.id}`}
                  onClick={() => navigate(`/show/${s.id}`)}
                  className="font-mono text-[11px] lowercase border-2 border-white px-2 py-2 hover:bg-white hover:text-black transition-colors"
                >
                  open
                </button>
                <button
                  data-testid={`saved-remove-${s.id}`}
                  onClick={() => {
                    removeSave(s.id);
                    toast("removed", { duration: TOAST_MS.NORMAL });
                  }}
                  className="font-mono text-[11px] lowercase border-2 border-white/40 text-white/60 px-2 py-2 hover:border-white hover:text-white transition-colors flex items-center justify-center gap-1"
                >
                  <X className="w-3 h-3" /> remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
